import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Film, PenTool, ShieldCheck } from "lucide-react";
import { MaskedLines } from "./MaskedText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Who can register as a vendor?",
    a: "Any registered brand, manufacturer, artisan, or FPO based in India — with a special focus on authentic Bengali products. You'll need a valid PAN, GST (if applicable), and a bank account in the business name.",
  },
  {
    q: "What documents are required to onboard?",
    a: "PAN card, cancelled cheque or bank passbook (first page), and — if you are GST-registered — your GST certificate. Payout bank details are collected separately, only after your application is approved, via a private secure link.",
  },
  {
    q: "How much commission does Sonar Bangla Haat take?",
    a: "0% on your first 30 sales as a Founding Vendor. Thereafter, a tiered 7%–9% commission depending on your category — no flat cut, no hidden listing fees, no surprise deductions.",
  },
  {
    q: "How long does approval take?",
    a: "Our team reviews every application within 24 hours. Once approved, your branded storefront goes live within 48–72 hours and we personally guide you through your first product listing.",
  },
  {
    q: "When and how do I receive payouts?",
    a: "Payouts settle on a regular weekly cycle directly to your registered bank account, with a transparent statement of every sale, fee, and adjustment. Bank details are collected via a private, encrypted link only after approval.",
  },
];

const CHAPTERS = [
  {
    num: "01",
    eyebrow: "The Reel",
    title: "One free promotional reel — crafted for every premium vendor.",
    body: "Our in-house studio produces a professional cinematic reel that lifts your brand out of the marketplace grid and into a story worth watching.",
    Icon: Film,
  },
  {
    num: "02",
    eyebrow: "The Story",
    title: "A written brand story, tuned for search.",
    body: "Every founding vendor receives a professionally written, SEO-optimised brand story page — the kind that makes strangers feel like regulars.",
    Icon: PenTool,
  },
  {
    num: "03",
    eyebrow: "The Guardrails",
    title: "FSSAI-compliant listings. Secure payouts. Transparent settlement.",
    body: "Your inventory, your money, your reputation — all guarded by a compliant, transparent process from day one.",
    Icon: ShieldCheck,
  },
];

export default function Slide2Perks({ onNext, onBack }) {
  return (
    <div className="w-full flex flex-col" data-testid="slide-2-perks">
      <motion.div
        className="eyebrow"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Chapter 02 · The Founding Vendor Manifesto
      </motion.div>

      <h2 className="mt-3 md:mt-4 font-editorial text-[36px] sm:text-[48px] md:text-[60px] leading-[1.05] tracking-[-0.01em] max-w-4xl">
        <MaskedLines
          lines={[
            "Three quiet promises",
            <span key="i" className="italic text-[color:var(--sbh-terracotta)]">we make first.</span>,
          ]}
          stagger={0.16}
        />
      </h2>

      <div className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10">
        {CHAPTERS.map((c, i) => (
          <motion.article
            key={c.num}
            className="relative pt-6 border-t border-[color:var(--sbh-border)]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.6 + i * 0.15,
              ease: [0.22, 1, 0.36, 1],
            }}
            data-testid={`perk-chapter-${c.num}`}
          >
            {/* Editorial numeral — dedicated top block, no overlap */}
            <div className="flex items-baseline justify-between mb-4">
              <span
                className="font-editorial font-semibold text-[72px] md:text-[92px] leading-[0.9] text-[color:var(--sbh-terracotta)]"
                aria-hidden="true"
              >
                {c.num}
              </span>
              <span className="eyebrow whitespace-nowrap">{c.eyebrow}</span>
            </div>
            <div className="flex items-center gap-3">
              <c.Icon
                className="w-5 h-5 text-[color:var(--sbh-mustard)]"
                strokeWidth={1.6}
              />
              <span className="text-[11px] tracking-[0.28em] uppercase text-[color:var(--sbh-muted)]">
                Chapter {c.num}
              </span>
            </div>
            <h3 className="mt-4 font-editorial text-[24px] md:text-[28px] leading-[1.15] text-[color:var(--sbh-ink)] max-w-[22ch]">
              {c.title}
            </h3>
            <p className="mt-4 text-[14.5px] leading-relaxed text-[color:var(--sbh-ink-2)] max-w-[36ch]">
              {c.body}
            </p>
          </motion.article>
        ))}
      </div>

      {/* Trust / returns policy note as elegant editorial footnote */}
      <motion.aside
        className="mt-14 md:mt-20 relative max-w-4xl border-l-2 border-[color:var(--sbh-terracotta)] pl-6 md:pl-8 py-2"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 1.2 }}
        data-testid="returns-policy-note"
      >
        <span className="eyebrow">Footnote · Customer Returns Policy</span>
        <p className="mt-3 font-editorial italic text-[19px] md:text-[22px] leading-[1.45] text-[color:var(--sbh-ink)]">
          &ldquo;To protect our vendors, returns are accepted only within 7 days of delivery, and only with an unboxing verification video. Your products are protected from false claims.&rdquo;
        </p>
      </motion.aside>

      {/* FAQ — editorial accordion */}
      <section
        className="mt-16 md:mt-24 max-w-4xl"
        data-testid="perks-faq"
      >
        <div className="eyebrow">Frequently asked · Before you apply</div>
        <h3 className="mt-3 font-editorial text-[28px] md:text-[36px] leading-[1.1] tracking-[-0.01em]">
          Answers to the five questions
          <span className="italic text-[color:var(--sbh-terracotta)]"> every vendor</span>
          <span className="italic text-[color:var(--sbh-terracotta)]"> asks first.</span>
        </h3>
        <Accordion type="single" collapsible className="mt-8">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-t border-[color:var(--sbh-border)] last:border-b"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="py-5 md:py-6 text-left font-editorial text-[19px] md:text-[22px] leading-snug text-[color:var(--sbh-ink)] hover:no-underline">
                <span className="flex items-start gap-4">
                  <span className="font-editorial text-[color:var(--sbh-mustard)] text-[14px] mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{f.q}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-10 text-[14.5px] md:text-[15px] leading-relaxed text-[color:var(--sbh-ink-2)] max-w-[62ch]">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <div className="mt-12 md:mt-16 flex items-center justify-between gap-6">
        <button className="btn-ghost" onClick={onBack} data-testid="perks-back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back
        </button>
        <button className="btn-editorial" onClick={onNext} data-testid="perks-continue-btn">
          Continue to Business Details
          <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
